/**
 * CMP設定管理・バリデーション
 *
 * 初期化時に設定JSONをバリデーションし、不正な値を検出する。
 * 必須項目が欠落している場合はエラーを出力して初期化を中断する。
 */

const REQUIRED_KEYS = ['categories', 'translations', 'defaultLanguage'];

/**
 * 設定オブジェクトをバリデーションする
 * @param {Object} raw - パース済みの設定オブジェクト
 * @returns {{ valid: boolean, config: Object|null, errors: string[] }}
 */
export function validateConfig(raw) {
  const errors = [];

  if (!raw || typeof raw !== 'object') {
    return { valid: false, config: null, errors: ['CMP config is not an object'] };
  }

  // 必須キーチェック
  for (const key of REQUIRED_KEYS) {
    if (!(key in raw)) {
      errors.push(`Missing required config key: "${key}"`);
    }
  }

  if (!Array.isArray(raw.categories)) {
    errors.push('"categories" must be an array');
  }

  if (typeof raw.translations !== 'object' || raw.translations === null) {
    errors.push('"translations" must be an object');
  }

  if (typeof raw.defaultLanguage !== 'string') {
    errors.push('"defaultLanguage" must be a string');
  }

  if (errors.length > 0) {
    return { valid: false, config: null, errors };
  }

  // カテゴリバリデーション（不正なカテゴリは除外して続行）
  const validCategories = [];
  for (const cat of raw.categories) {
    if (!cat || typeof cat !== 'object' || typeof cat.key !== 'string' || !cat.key) {
      console.error('[CMP] Invalid category definition, skipping:', cat);
      continue;
    }
    validCategories.push({
      key: cat.key,
      required: Boolean(cat.required),
      gtmSignals: Array.isArray(cat.gtmSignals) ? cat.gtmSignals.filter((s) => typeof s === 'string') : []
    });
  }

  if (validCategories.length === 0) {
    return { valid: false, config: null, errors: ['No valid categories defined'] };
  }

  // Cookie設定のバリデーション・デフォルト化
  const cookieName = (raw.cookie && raw.cookie.name) || 'ck_cmp';
  if (!/^[a-zA-Z0-9_-]+$/.test(cookieName)) {
    errors.push('"cookie.name" contains invalid characters');
  }

  const maxAge = (raw.cookie && raw.cookie.maxAge) || 180;
  if (typeof maxAge !== 'number' || !Number.isFinite(maxAge) || maxAge <= 0) {
    errors.push('"cookie.maxAge" must be a positive number');
  }

  const VALID_SAME_SITE = ['Strict', 'Lax', 'None'];
  const sameSite = (raw.cookie && raw.cookie.sameSite) || 'Lax';
  if (!VALID_SAME_SITE.includes(sameSite)) {
    errors.push(`"cookie.sameSite" must be one of: ${VALID_SAME_SITE.join(', ')}`);
  }

  if (errors.length > 0) {
    return { valid: false, config: null, errors };
  }

  const config = {
    categories: validCategories,
    defaultLanguage: raw.defaultLanguage,
    translations: raw.translations,
    consentLogUrl: raw.consentLogUrl || null,
    privacyUrl: raw.privacyUrl || null,
    cookie: {
      name: cookieName,
      maxAge,
      path: (raw.cookie && raw.cookie.path) || '/',
      sameSite
    }
  };

  if (raw.gtmConsentMode && typeof raw.gtmConsentMode === 'object') {
    config.gtmConsentMode = { enabled: Boolean(raw.gtmConsentMode.enabled) };
  }

  return { valid: true, config, errors: [] };
}
